import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { RefreshCw, CameraOff, Play } from "lucide-react";

/**
 * Self-hosted HLS webcam player.
 *
 * Loads a stream from `${REACT_APP_BACKEND_URL}${streamUrl}` (e.g.
 * /api/webcams/hutchison/playlist.m3u8). Uses native HLS on Safari/iOS and
 * hls.js everywhere else. The `refreshKey` prop forces a full re-init when
 * the user clicks the refresh button.
 *
 * Error handling is resilient by design for flaky cameras: hls.js is never
 * destroyed on transient network errors — it keeps retrying indefinitely.
 * A watchdog tracks the time since the last successful fragment; if the
 * stream stalls for more than STALE_THRESHOLD_MS the badge flips to
 * "reconnecting" and a friendly overlay appears. As soon as fragments start
 * loading again it automatically switches back to "live" with no user
 * action required.
 *
 * The container uses CSS `aspect-ratio` matching the camera's native
 * frame (passed via the `aspectRatio` prop) so the full video fills the
 * frame edge-to-edge with no cropping and no letterbox bars. If metadata
 * reveals a different actual ratio at runtime, we update to match.
 *
 * Playback is gated behind an explicit user click. On initial render the
 * component shows a poster image (typically the camera's latest snapshot)
 * with a large play button overlay. The hls.js pipeline isn't initialised
 * and no segments are fetched until the user hits play, so passive visitors
 * cost nothing in bandwidth or CPU.
 */
const STALE_THRESHOLD_MS = 12000;

const WebcamPlayer = ({
  streamUrl,
  refreshKey,
  onRefresh,
  aspectRatio = "16 / 9",
  posterUrl = null,
  className = "",
}) => {
  const videoRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | live | stale | offline
  const [measuredRatio, setMeasuredRatio] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);

  // Reset the "has started" gate when the user clicks refresh so the
  // preview-with-play-button comes back instead of silently reconnecting.
  useEffect(() => {
    setHasStarted(false);
  }, [refreshKey]);

  useEffect(() => {
    if (!hasStarted) return;
    const video = videoRef.current;
    if (!video || !streamUrl) return;

    const fullUrl = `${process.env.REACT_APP_BACKEND_URL || ""}${streamUrl}`;
    let hls = null;
    let lastFragAt = Date.now();
    let watchdogId = null;

    setStatus("loading");

    const handleLoadedMetadata = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setMeasuredRatio(`${video.videoWidth} / ${video.videoHeight}`);
      }
    };
    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    const startWatchdog = () => {
      if (watchdogId) return;
      watchdogId = setInterval(() => {
        const age = Date.now() - lastFragAt;
        setStatus((prev) => {
          if (age > STALE_THRESHOLD_MS) {
            return prev === "offline" ? prev : "stale";
          }
          return prev;
        });
      }, 3000);
    };

    // --- Native HLS path (Safari / iOS) -------------------------------
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = fullUrl;

      const onPlaying = () => {
        lastFragAt = Date.now();
        setStatus("live");
      };
      const onTimeUpdate = () => {
        lastFragAt = Date.now();
      };
      const onWaiting = () => {
        // Keep "live" while buffering briefly; watchdog will catch long stalls
      };
      const onError = () => {
        // Safari reloads don't auto-recover from a broken src — nudge it
        try {
          video.load();
          video.play().catch(() => {});
        } catch (e) {
          /* noop */
        }
      };

      video.addEventListener("playing", onPlaying);
      video.addEventListener("timeupdate", onTimeUpdate);
      video.addEventListener("waiting", onWaiting);
      video.addEventListener("error", onError);
      video.play().catch(() => {});
      startWatchdog();

      return () => {
        if (watchdogId) clearInterval(watchdogId);
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        video.removeEventListener("playing", onPlaying);
        video.removeEventListener("timeupdate", onTimeUpdate);
        video.removeEventListener("waiting", onWaiting);
        video.removeEventListener("error", onError);
        video.removeAttribute("src");
        video.load();
      };
    }

    // --- hls.js path (Chrome / Firefox / Edge / etc.) -----------------
    if (Hls.isSupported()) {
      hls = new Hls({
        liveDurationInfinity: true,
        lowLatencyMode: false,
        backBufferLength: 30,
        // Generous retry budgets — the camera is flaky, not broken
        manifestLoadingMaxRetry: 10,
        manifestLoadingRetryDelay: 1000,
        manifestLoadingMaxRetryTimeout: 64000,
        levelLoadingMaxRetry: 10,
        levelLoadingRetryDelay: 1000,
        levelLoadingMaxRetryTimeout: 64000,
        fragLoadingMaxRetry: 10,
        fragLoadingRetryDelay: 1000,
        fragLoadingMaxRetryTimeout: 64000,
      });
      hls.loadSource(fullUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });

      hls.on(Hls.Events.FRAG_LOADED, () => {
        lastFragAt = Date.now();
        setStatus("live");
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            // Never destroy — keep retrying forever. The watchdog surfaces
            // the stall to the user via the "stale" state.
            try {
              hls.startLoad();
            } catch (e) {
              /* noop */
            }
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            try {
              hls.recoverMediaError();
            } catch (e) {
              /* noop */
            }
            break;
          default:
            // Unrecoverable — rebuild from scratch after a short delay
            try {
              hls.destroy();
            } catch (e) {
              /* noop */
            }
            hls = null;
            setStatus("offline");
            break;
        }
      });

      startWatchdog();

      return () => {
        if (watchdogId) clearInterval(watchdogId);
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        if (hls) {
          try {
            hls.destroy();
          } catch (e) {
            /* noop */
          }
        }
      };
    }

    // Fallback: no HLS support at all (very old browsers)
    setStatus("offline");
    return undefined;
  }, [streamUrl, refreshKey, hasStarted]);

  const showOfflineOverlay = hasStarted && status === "offline";
  const showReconnectOverlay = hasStarted && status === "stale";
  const effectiveRatio = measuredRatio || aspectRatio;
  const posterFullUrl = posterUrl
    ? `${process.env.REACT_APP_BACKEND_URL || ""}${posterUrl}`
    : null;

  return (
    <div className={`relative bg-black overflow-hidden ${className}`}>
      <div className="w-full" style={{ aspectRatio: effectiveRatio }}>
        <video
          ref={videoRef}
          muted
          playsInline
          controls={hasStarted}
          className={`w-full h-full object-contain bg-black ${
            hasStarted ? "" : "hidden"
          }`}
        />
        {!hasStarted && posterFullUrl && (
          <img
            src={posterFullUrl}
            alt="Webcam preview"
            className="w-full h-full object-contain bg-black"
          />
        )}
      </div>

      {!hasStarted && (
        <button
          type="button"
          onClick={() => setHasStarted(true)}
          className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 hover:bg-black/30 transition-colors group cursor-pointer"
          aria-label="Start live stream"
        >
          <div className="w-20 h-20 rounded-full bg-orange-500/90 group-hover:bg-orange-500 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
            <Play className="w-10 h-10 text-white fill-white ml-1" />
          </div>
          <p className="mt-4 text-white text-sm font-semibold drop-shadow-lg">
            Click to start live stream
          </p>
        </button>
      )}

      {showOfflineOverlay && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/85 text-center p-6 pointer-events-none">
          <CameraOff className="w-12 h-12 text-gray-400 mb-3" />
          <p className="text-white font-semibold text-lg">Camera temporarily offline</p>
          <p className="text-gray-300 text-sm mt-1">
            Try the refresh button to reconnect.
          </p>
        </div>
      )}

      {showReconnectOverlay && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-center p-6 pointer-events-none">
          <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-white font-semibold text-base">Reconnecting…</p>
          <p className="text-gray-300 text-xs mt-1">
            Stream will resume automatically.
          </p>
        </div>
      )}

      <div className="absolute top-4 left-4 pointer-events-none">
        {!hasStarted ? (
          <Badge className="bg-slate-700/80 backdrop-blur-sm">PREVIEW</Badge>
        ) : status === "live" ? (
          <Badge className="bg-red-600 hover:bg-red-700 animate-pulse">
            🔴 LIVE
          </Badge>
        ) : status === "stale" ? (
          <Badge className="bg-orange-600 animate-pulse">RECONNECTING…</Badge>
        ) : status === "offline" ? (
          <Badge className="bg-gray-600">OFFLINE</Badge>
        ) : (
          <Badge className="bg-yellow-600 animate-pulse">CONNECTING…</Badge>
        )}
      </div>
      {hasStarted && (
        <div className="absolute top-4 right-4">
          <Button
            size="sm"
            variant="secondary"
            className="bg-black/50 hover:bg-black/70 text-white border-0"
            onClick={onRefresh}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default WebcamPlayer;
